import DocumentsTable from '@/app/documents/DocumentsTable'
import TableSkeleton from '@/components/table-skeleton'
import { useDocumentsQuery } from '@/hooks/useDocumentsQuery'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { Can } from '@/context/auth/AbilityContext'
import type { SubjectName } from '@/context/auth/AbilityContext'
import { useMemo } from 'react'
import Constants from '@/constants/Constants'
import { ability } from '@/casl/ability'
import { getFirstAccessibleRoute } from '@/constants/Auth'
import type { IAppDocument } from '@/types/type'

const RoutePage = ({ fullPath }: { fullPath: string }) => {
  const { data: documents, isFetching: isDocumentsFetching } =
    useDocumentsQuery(fullPath, { refetchOnWindowFocus: false })

  if (isDocumentsFetching) {
    return <TableSkeleton />
  }

  const category = fullPath.split('/')[0]

  return (
    <DocumentsTable
      documents={documents as IAppDocument}
      category={category}
      path={fullPath}
    />
  )
}

const RouteComponent = () => {
  const { '*': fullPath } = Route.useParams()

  const category = useMemo(() => fullPath.split('/')[0], [fullPath])

  type CategoryKey = keyof typeof Constants.documentCategoriesMap

  const categoryObject = useMemo(() => {
    if (!category || !(category in Constants.documentCategoriesMap)) {
      return null
    }
    const sub = Constants.documentCategoriesMap[category as CategoryKey]
    if (!sub) {
      return null
    }
    return sub
  }, [category])

  if (!categoryObject) {
    return <div>Dokuman Kategorisi bulunamadi</div>
  }

  return (
    <Can I="read" a={categoryObject.subject as SubjectName}>
      <RoutePage fullPath={fullPath} />
    </Can>
  )
}

export const Route = createFileRoute('/_auth/_withProject/documents/$')({
  beforeLoad: ({ context, params }) => {
    const { _splat: fullPath } = params

    const category = fullPath?.split('/')[0]

    const categoryObj =
      Constants.documentCategoriesMap[
        category as keyof typeof Constants.documentCategoriesMap
      ]
    const subjectName = categoryObj?.subject

    if (
      ability &&
      context.auth.user &&
      (!subjectName || !ability.can('read', subjectName))
    ) {
      const permissions = context.auth.user.permissions || []
      const redirectPath = getFirstAccessibleRoute(permissions)
      throw redirect({
        to: redirectPath,
      })
    }
  },
  component: RouteComponent,
})
